import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { applyActionCode } from 'firebase/auth';
import { auth } from '../config/firebase';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    async function verifyEmail() {
      const oobCode = searchParams.get('oobCode');

      if (!oobCode) {
        setStatus('error');
        setErrorMessage('Invalid or missing verification code.');
        return;
      }

      try {
        await applyActionCode(auth, oobCode);
        setStatus('success');
      } catch (err: any) {
        console.error('Email verification error:', err);
        setStatus('error');

        switch (err.code) {
          case 'auth/expired-action-code':
            setErrorMessage('The verification link has expired. Please request a new one.');
            break;
          case 'auth/invalid-action-code':
            setErrorMessage('The verification link is invalid. It may have already been used.');
            break;
          case 'auth/user-disabled':
            setErrorMessage('This account has been disabled.');
            break;
          default:
            setErrorMessage('Failed to verify email. Please try again or contact support.');
        }
      }
    }

    verifyEmail();
  }, [searchParams]);

  if (status === 'verifying') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="text-6xl mb-4">⏳</div>
          <h1 className="text-2xl font-semibold text-primary mb-2">Verifying Your Email...</h1>
          <p className="text-secondary">Please wait while we verify your email address.</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-semibold text-primary mb-4">Verification Failed</h1>
          <p className="text-secondary mb-6">{errorMessage}</p>

          <div className="space-y-3">
            <Link
              to="/portal"
              className="block w-full bg-blue-600 text-white py-2 px-4 rounded-md font-medium hover:bg-blue-700 transition-colors"
            >
              Go to Portal
            </Link>
            <a
              href="mailto:support@dealvelocity.co.za"
              className="block w-full border border-gray-300 text-secondary py-2 px-4 rounded-md font-medium hover:bg-gray-50 transition-colors"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Success
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
        <div className="text-6xl mb-4">✅</div>
        <h1 className="text-2xl font-semibold text-primary mb-4">Email Verified!</h1>
        <p className="text-secondary mb-6">
          Your email address has been successfully verified. You can now access all features of your account.
        </p>

        <Link
          to="/portal"
          className="inline-block w-full bg-blue-600 text-white py-3 px-6 rounded-md font-medium hover:bg-blue-700 transition-colors"
        >
          Go to Portal
        </Link>

        <p className="text-sm text-secondary mt-6">
          You can now download your purchased products and access all services.
        </p>
      </div>
    </div>
  );
}
